import { TransactionStatus, TransactionStatusDescriptions, TransactionTypes } from "../../../../enum/Enum";

// Parameter field names, shared by Layout.js (the schema) and the page (formRefs lookups, the
// Transaction Date Method rule and the three Start confirmations). Declared once so a rename cannot
// silently break the page's imperative field access, which is keyed by these exact strings.
//
// SlidingDays keeps the Delphi control name (edtSlidingDays) rather than its caption, so the legacy
// unit stays greppable from here.
export const FIELD = Object.freeze({
    DateMethod: "DateMethod",
    DayOfNextMonth: "DayOfNextMonth",
    SlidingDays: "SlidingDays",
    Status: "Status",
});

// WMD0094 "Transaction Date Method" combo (uWMD0094.dfm cmbMethod; ItemIndex 0/1 mapped to 'F'/'S' in
// MainAction) -> @InDateMethod. Mirrors Argos.Core/Constants/RepeatingMiscChargesConstants.cs ->
// RepeatingChargeDateMethod.
export const DateMethod = Object.freeze({
    Fixed: "F",
    Sliding: "S",
});

// Captions verbatim from uWMD0094.dfm cmbMethod.Items, in DFM order. Fixed is the default:
// FormAbLoadProgram sets cmbMethod.ItemIndex := 0.
export const DATE_METHOD_OPTIONS = [
    { key: DateMethod.Fixed, value: "Fixed" },
    { key: DateMethod.Sliding, value: "Sliding" },
];

/**
 * WMTRANSACTIONHEADER.STATUS captions AS THIS SCREEN LABELS THEM.
 *
 * 'L' is "Ready To Invoice" here, taken from uWMD0094.dfm cmbStatus.Items. Every other WM screen calls
 * the same code "Ready To Commit Later" (TransactionStatusDescriptions.ReadyToCommitLater). The
 * divergence is in AI8, not introduced here; this screen's own caption wins so the migrated screen reads
 * like the one it replaces, and the single caption is used in BOTH the parameter combo and the preview
 * grid so the screen is at least self-consistent (Architecture.md rule 13).
 *
 * Codes come from TransactionStatus, never from literals (Architecture.md R2).
 */
export const STATUS_CAPTION = Object.freeze({
    [TransactionStatus.Open]: TransactionStatusDescriptions.Open,
    [TransactionStatus.Hold]: TransactionStatusDescriptions.Hold,
    [TransactionStatus.ReadyToCommitLater]: "Ready To Invoice",
    [TransactionStatus.Committed]: TransactionStatusDescriptions.Committed,
});

// The parameter combo: the three codes the Delphi offers, in its order. ItemIndex 2 (= 'L') is the
// default. Committed is deliberately absent — it is a legal status on a SOURCE transaction (hence
// present in the grid lookup below) but the operation may not stamp it on a generated one.
export const STATUS_OPTIONS = [
    { key: TransactionStatus.Open, value: STATUS_CAPTION[TransactionStatus.Open] },
    { key: TransactionStatus.Hold, value: STATUS_CAPTION[TransactionStatus.Hold] },
    { key: TransactionStatus.ReadyToCommitLater, value: STATUS_CAPTION[TransactionStatus.ReadyToCommitLater] },
];

// Preview grid <Lookup> source. DevExtreme's default lookup keys, hence id/text rather than the
// key/value pair the dynamic-form SelectBox uses above — the two shapes are not interchangeable.
//
// ALL FOUR codes, unlike the combo. STATUS is NOT pinned by the where-builder's base filter (that is
// only STAGE='A' AND TRANSACTIONTYPE='M' AND RECURRINGCHARGE='Y'), so it varies row to row and a
// committed source transaction can legitimately come back. A DevExtreme lookup renders an EMPTY CELL
// for a value missing from its dataSource, so omitting 'Z' would silently blank those rows.
export const STATUS_GRID_OPTIONS = [
    { id: TransactionStatus.Open, text: STATUS_CAPTION[TransactionStatus.Open] },
    { id: TransactionStatus.Hold, text: STATUS_CAPTION[TransactionStatus.Hold] },
    { id: TransactionStatus.ReadyToCommitLater, text: STATUS_CAPTION[TransactionStatus.ReadyToCommitLater] },
    { id: TransactionStatus.Committed, text: STATUS_CAPTION[TransactionStatus.Committed] },
];

// Transaction Type is always 'M' — the procedure's base filter pins TRANSACTIONTYPE = 'M'. A one-entry
// lookup is what keeps a Delphi-visible column from rendering a bare code (Architecture.md rule 3).
//
// The code itself comes from the shared enum (TransactionTypes.Miscellaneous), not a local map: 'M' is a
// WMTRANSACTIONHEADER.TRANSACTIONTYPE value like every other member of that map, so a screen-private
// copy would be the duplication rule R2 exists to prevent. Only the caption is declared here.
export const TRANS_TYPE_OPTIONS = [{ id: TransactionTypes.Miscellaneous, text: "Miscellaneous" }];

// uWMD0094.dfm edtDay: MinValue 1, MaxValue 31, Value 1 (re-asserted by SetDefaultSettings). Fields.js's
// NumberBox takes no min/max from the layout schema, so the range is enforced by the page's
// clampDayOfNextMonth and again server-side by [Range] on the request DTO.
export const DAY_OF_NEXT_MONTH_MIN = 1;
export const DAY_OF_NEXT_MONTH_MAX = 31;
export const DEFAULT_DAY_OF_NEXT_MONTH = DAY_OF_NEXT_MONTH_MIN;

// uWMD0094.dfm edtSlidingDays: no MinValue/MaxValue (the control accepts negatives), Value 0.
export const DEFAULT_SLIDING_DAYS = 0;

// Matches the DisplayFormat ",0;-,0" that BOTH TArgosNumberCombos carry. Fields.js honours `format`
// only for type "number", which is what both of these are.
export const SIGNED_INTEGER_FORMAT = "#,##0;-#,##0";

/**
 * The two "Additional Information" memos, keyed by the selected method — cmbMethodChange swaps which
 * one is visible. Same shape as the sibling screen's DEFERRED_REVENUE_HELP map.
 *
 * Verbatim from uWMD0094.dfm memMethodFixed / memMethodSliding, with two knowing exceptions: trailing
 * spaces are dropped (they are invisible), and the DOUBLE space in
 * "# Of Days From Trans. Date  = 10" is KEPT because it is in the DFM. Do not tidy it.
 */
export const HELP = Object.freeze({
    [DateMethod.Fixed]:
        "The Fixed Method will set the Transaction Date, to the Day Of the Next Month.\n" +
        "Example:\n" +
        "Transaction Date = 01/17/2004\n" +
        "Day Of Next Month (user entered) = 25\n" +
        "Transaction Date of generated transaction = 02/25/2004",
    [DateMethod.Sliding]:
        'The Sliding Method will add the "Number of Days" to the Transaction Date.\n' +
        "Example:\n" +
        "Transaction Date = 01/17/2004\n" +
        "# Of Days From Trans. Date  = 10\n" +
        "Transaction Date of generated transaction = 01/27/2004",
});

// uWMD0094.dfm AbColumnLabel1.Caption — the header over the help memo (grpBottomMiddle).
export const HELP_TITLE = "Additional Information";

// Saved-layout identity for the Preview grid, and GridLayoutManager's built-in layout name used as the
// initial currentLayout state.
export const PREVIEW_GRID_KEY = "9B4E7C12-6A35-4D80-91FE-2C7B4A8D5E36";
export const DEFAULT_LAYOUT_NAME = "Default";

// Dynamic-form layout identity for the parameters panel.
export const LAYOUT_ID = "4D71E2A8-3F60-4C95-8B27-6E1A9D0C5F84";
export const LAYOUT_NAME = "WMGenerateRepeatingMiscChargesForm";

// Filter-field definitions come from Argos.Core/Report/WM/WMGenerateRepeatingMiscCharges.cs via
// ReportFactory's "WMGENERATEREPEATINGMISCCHARGES" case.
export const REPORT_NAME = "WMGenerateRepeatingMiscCharges";
export const REPORT_FULL_NAME = "Argos.Report.WM.WMGenerateRepeatingMiscCharges, Argos.Report.WM";

// One consistent display name everywhere (Architecture.md rule 13): page title, sidebar text, Home
// search entries and the permission description. The full AI8 caption was "Generate Repeating
// Miscellaneous Transaction Charges"; the shortened form matches the identifiers and the URL slug.
export const PAGE_TITLE = "Generate Repeating Miscellaneous Charges";

export const PREVIEW_TITLE = "Preview";

/**
 * The legacy result dialog is TfrmABD0013, whose caller-supplied caption WMD0094 passes as "Replicate
 * Transaction Charges". The web title reuses MESSAGES.PROCESS_COMPLETE and drops the "(ABD0013)" form
 * id, which means nothing outside Delphi.
 *
 * ONE row: WMD0094 hides lblBatch/edtBatch and moves the record label up, because the procedure creates
 * no ACBatch row — so the batch half of the shared dialog is not part of this screen.
 */
export const RESULT_CAPTION = "Replicate Transaction Charges";
export const RESULT_LABELS = Object.freeze({ TransactionsReplicated: "Transactions Replicated" });
export const RESULT_OK = "OK";

// Toolbar captions, verbatim from uWMD0094.dfm grpBottomButtons (accelerator ampersands stripped).
// Help and Exit are not migrated: Help opened a Delphi context topic and Exit closed the MDI child.
// Same decision as both sibling operation screens.
export const BUTTON = Object.freeze({
    Start: "Start",
    Preview: "Preview",
    Reset: "Reset",
});

// The procedure replicates headers AND their charge lines, and the Delphi wrapped it in an indefinite
// wait dialog. Must exceed the server's own command timeout for the procedure (900s).
export const REQUEST_TIMEOUT_MS = 20 * 60 * 1000;
